import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
    Grid,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useHistory, useLocation, Link } from 'react-router-dom';

import TablePager from '../../components/atom/TablePager';
import SortableTableCell from '../../components/atom/SortableTableCell';
import FilterLine from '../../components/filter/FilterLine';
import { useSorting } from '../../hooks/useSorting';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import UnconfirmedBookingTableRow from './UnconfirmedBookingTableRow';
import {
    UNCONFIRMED_BOOKINGS_PAGE_FILTERS,
    getFilterFromUrl,
    getUrlFromFilter
} from './UnconfirmedBookingsFilter';
import { loginAsCustomerWithId } from '../../actions/user';
import { useDispatch } from 'react-redux';
import { useModals } from '../../hooks/useModal';
import { normalizeFilters } from '../../utils/filters';

function UnconfirmedBookingsPage({ customer, cartItemCount }) {
    const history = useHistory();
    const location = useLocation();
    const { showModal } = useModals();

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));
    const { sort, sortDir, setSorting } = useSorting(['activity_start', 'DESC']);

    const dispatch = useDispatch();

    const setCurrentFilters = useCallback(
        newFilters => history.replace(getUrlFromFilter(newFilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
        setPage(0);
    }, [location.search]);

    const [data, loading, error] = useSwaggerClient('bookings', 'getBookingItemsForClients', {
        page,
        pageSize,
        sort,
        sortDir,
        bookingId: currentFilters.bookingId || '',
        vendorList: currentFilters.vendor || '',
        agentList: currentFilters.agent || '',
        guestList: currentFilters.guest || '',
        activityList: currentFilters.activity || '',
        fromDate: moment(currentFilters.checkInDate).isValid()
            ? moment(currentFilters.checkInDate).format('YYYY-MM-DD')
            : '',
        toDate: moment(currentFilters.checkOutDate).isValid()
            ? moment(currentFilters.checkOutDate).format('YYYY-MM-DD')
            : '',
        statusList: ['unconfirmed'],
        isExpired: true
    });

    const resetFilters = () =>
        setCurrentFilters({
            bookingId: '',
            vendor: null,
            agent: null,
            guest: null,
            activity: null,
            checkInDate: null,
            checkOutDate: null
        });

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.booking_items?.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    const normalizedFilters = normalizeFilters(currentFilters);

    return (
        <Grid container spacing={2} className="golf-reservation">
            <Grid item xs={12}>
                <FilterLine
                    possibleFilters={UNCONFIRMED_BOOKINGS_PAGE_FILTERS}
                    filters={normalizedFilters}
                    setFilters={setCurrentFilters}
                    defaultValues={[
                        {
                            key: 'bookingId',
                            label: 'Reservation ID',
                            type: 'text',
                            value: ''
                        }
                    ]}
                    resetFilters={resetFilters}
                />
            </Grid>

            <Grid item xs={12}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <TableContainer style={{ overflowX: 'auto' }}>
                            <Table
                                className="mt-2"
                                style={{ backgroundColor: 'white' }}
                                size="small"
                            >
                                <TableHead>
                                    <TableRow>
                                        <SortableTableCell
                                            arrow={sort === 'guest' ? sortDir : ''}
                                            onClick={() => setSorting('guest')}
                                        >
                                            Guest
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'agent' ? sortDir : ''}
                                            onClick={() => setSorting('agent')}
                                        >
                                            Agent
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'vendor' ? sortDir : ''}
                                            onClick={() => setSorting('vendor')}
                                        >
                                            Vendor
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'id' ? sortDir : ''}
                                            onClick={() => setSorting('id')}
                                        >
                                            Reservation ID
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activity' ? sortDir : ''}
                                            onClick={() => setSorting('activity')}
                                        >
                                            Activity
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activity_start' ? sortDir : ''}
                                            onClick={() => setSorting('activity_start')}
                                        >
                                            Activity start
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'amount' ? sortDir : ''}
                                            onClick={() => setSorting('amount')}
                                        >
                                            Amount
                                        </SortableTableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {error && (
                                        <TableRow>
                                            <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                                                <Alert severity="error">
                                                    <h4>
                                                        {error?.response?.body?.message ||
                                                            error?.message}
                                                    </h4>
                                                </Alert>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!error &&
                                        (data?.booking_items || []).map(bookingItem => (
                                            <UnconfirmedBookingTableRow
                                                key={bookingItem.id}
                                                booking_item={bookingItem}
                                                customer={customer}
                                                loginAsCustomerWithId={(
                                                    customerId,
                                                    showModal,
                                                    onError,
                                                    onSuccess
                                                ) =>
                                                    dispatch(
                                                        loginAsCustomerWithId(
                                                            customerId,
                                                            showModal,
                                                            onError,
                                                            onSuccess
                                                        )
                                                    )
                                                }
                                                cartItemCount={cartItemCount}
                                                showModal={showModal}
                                            />
                                        ))}

                                    {!error && (data?.booking_items || []).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center">
                                                No unconfirmed bookings found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Grid
                            style={{
                                display: 'flex',
                                gap: 20,
                                paddingTop: 12,
                                paddingLeft: 12
                            }}
                        >
                            <Link to="/client/bookings" target="_blank">
                                <Typography style={{ cursor: 'pointer' }} color="primary">
                                    View all bookings
                                </Typography>
                            </Link>
                        </Grid>

                        <Grid container justifyContent="flex-end">
                            <TablePager
                                total={data?.totalExpired}
                                page={page}
                                setPage={setPage}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
}

export default UnconfirmedBookingsPage;
